import React, { useCallback, useState } from 'react';
import type { ColumnFiltersState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { Card, CardContent } from '@/components/ui-kit/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Skeleton } from '@/components/ui-kit/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { formatDateOnly, toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import { filterRowsByColumns } from '@/lib/table-query';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-media-query';
import { useInitialPageSize } from '@/hooks/use-server-table';
import { createLeaveSheetTableColumns } from '../leave-sheet-table/leave-sheet-table-columns';
import {
  useGetLeaveSheet,
  useGetLeaveSheetTerms,
  useLeaveSheetPolicy,
} from '../../hooks/use-leave-sheet';
import { LeaveSheetResult, LeaveSheetTerm } from '../../types/leave-sheet.types';

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

const ROWS_PER_PAGE = 50;

/** "HR Term <period>" for a term picker. Built from the period number, not `HRPeriodName`: that is
 * stored as untranslated "HR Term <n>" and would repeat the prefix. */
export const termLabel = (term: LeaveSheetTerm, t: (key: string) => string) => {
  if (term.HRPeriod) return `${t('HR_TERM')} ${term.HRPeriod}`;
  if (term.HRPeriodName) return term.HRPeriodName;
  return `${t('HR_TERM')} ${formatDateOnly(toDateOnlyValue(term.periodStart))} - ${formatDateOnly(toDateOnlyValue(term.periodEnd))}`;
};

/** A summary figure's tile color on a phone (legacy mobileLeaveSummeryCellColors / mobileTOILCellColors). */
type SummaryTone = 'total' | 'taken' | 'toil' | 'remaining' | 'earned';

const TILE_TONE: Record<SummaryTone, string> = {
  total: 'bg-primary-50',
  taken: 'bg-error-background',
  toil: 'bg-warning-background',
  remaining: 'bg-success-background',
  earned: 'bg-secondary-50',
};

type SummaryCell = [label: string, value: string | number, tone?: SummaryTone];

/** "N Day(s)" of a number of days. */
const daysText = (value: number, t: (key: string) => string) =>
  `${value} ${t(Math.abs(value) > 1 ? 'DAYS' : 'DAY')}`;

/** A section's heading on a phone, as the legacy mobile sheet. */
const MobileHeading = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-base font-bold">{children}</h2>
);

/**
 * The summary on a phone: two tinted tiles per row, the days large over their label (the legacy
 * mobile leave/TOIL summary). Cells without a number (the leave type) are left out.
 */
const SummaryTiles = ({ title, cells }: { title: string; cells: SummaryCell[] }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <MobileHeading>{title}</MobileHeading>
      <div className="grid grid-cols-2 gap-3">
        {cells
          .filter(([, value]) => typeof value === 'number')
          .map(([label, value, tone]) => (
            <div
              key={label}
              className={cn(
                'flex min-w-0 flex-col gap-1 rounded-2xl p-4',
                TILE_TONE[tone ?? 'total']
              )}
            >
              <span className="text-xl font-bold tabular-nums text-foreground">
                {daysText(value as number, t)}
              </span>
              <span className="break-words text-[13px] text-muted-foreground">{label}</span>
            </div>
          ))}
      </div>
    </div>
  );
};

/** One row of labels over one row of values, like the legacy summary tables. */
const SummaryTable = ({ cells }: { cells: SummaryCell[] }) => (
  <div className="overflow-x-auto rounded-md border">
    <Table>
      <TableHeader>
        <TableRow>
          {cells.map(([label]) => (
            <TableHead key={label} className="text-center">
              {label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow>
          {cells.map(([label, value]) => (
            <TableCell key={label} className="text-center font-medium tabular-nums">
              {value}
            </TableCell>
          ))}
        </TableRow>
      </TableBody>
    </Table>
  </div>
);

/** Per-category allotment: the default leave first (its allotment net of TOIL), then the others. */
const CategoryTable = ({ sheet }: { sheet: LeaveSheetResult }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { summary, categories } = sheet;
  const rows = [
    ...(summary
      ? [
          {
            id: 'default',
            name: summary.defaultCategory,
            unlimited: false,
            allocated: summary.allocated,
            taken: summary.allocated - summary.remainingLeave,
            remaining: summary.remainingLeave,
          },
        ]
      : []),
    ...categories.map((category) => ({
      id: category.LeaveCategoryId,
      name: category.LeaveCategory,
      unlimited: category.IsUnlimited,
      allocated: category.Allocated,
      taken: category.Taken,
      remaining: category.Remaining,
    })),
  ];
  if (rows.length === 0) return null;

  // On a phone each category is a card: "Name (N Days)", then taken and remaining (legacy mobile sheet).
  if (isMobile) {
    const limitOf = (row: (typeof rows)[number], value: number) =>
      row.unlimited ? t('UNLIMITED') : daysText(value, t);
    return (
      <div className="flex flex-col gap-3">
        <MobileHeading>{t('LEAVE_DETAILS')}</MobileHeading>
        {rows.map((row) => (
          <div key={row.id} className="flex flex-col gap-3 rounded-lg border p-4 text-sm">
            <span className="break-words font-semibold">
              {row.name} ({limitOf(row, row.allocated)})
            </span>
            <div className="grid grid-cols-2 gap-2">
              <span className="text-muted-foreground">{t('LEAVE_TAKEN')}</span>
              <span>{daysText(row.taken, t)}</span>
              <span className="text-muted-foreground">{t('REMAINING_LEAVE')}</span>
              <span>{limitOf(row, row.remaining)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('TYPE_OF_LEAVE')}</TableHead>
            <TableHead className="text-center">{t('ALLOCATED_LEAVE')}</TableHead>
            <TableHead className="text-center">{t('LEAVE_TAKEN')}</TableHead>
            <TableHead className="text-center">{t('REMAINING_LEAVE')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell className="font-medium">{row.name}</TableCell>
              <TableCell className="text-center tabular-nums">
                {row.unlimited ? t('UNLIMITED') : row.allocated}
              </TableCell>
              <TableCell className="text-center tabular-nums">{row.taken}</TableCell>
              <TableCell className="text-center tabular-nums">
                {row.unlimited ? t('UNLIMITED') : row.remaining}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/** The default leave's totals, with the TOIL figures the employee's `toilBalance` rule calls for. */
const LeaveSummary = ({ sheet }: { sheet: LeaveSheetResult }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { summary, policy } = sheet;
  if (!summary) return null;
  const category = summary.defaultCategory;

  if (policy.toil === 'split') {
    // Weekend and special side by side on a phone, the totals first (legacy mobile TOIL summary).
    const cells: SummaryCell[] = isMobile
      ? [
          [t('TOIL_CF'), summary.toilCarryForward, 'remaining'],
          [t('TOTAL_TOIL_REMAINING'), summary.toilLeft, 'taken'],
          [t('WEEKEND_TOIL_EARNED'), summary.weekendToilEarned, 'earned'],
          [t('SPECIAL_TOIL_EARNED'), summary.specialToilEarned, 'earned'],
          [t('WEEKEND_TOIL_TAKEN'), summary.weekendToilTaken, 'total'],
          [t('SPECIAL_TOIL_TAKEN'), summary.specialToilTaken, 'total'],
          [t('WEEKEND_TOIL_REMAINING'), summary.weekendToilLeft, 'toil'],
          [t('SPECIAL_TOIL_REMAINING'), summary.specialToilLeft, 'toil'],
        ]
      : [
          [t('TOIL_CF'), summary.toilCarryForward],
          [t('WEEKEND_TOIL_EARNED'), summary.weekendToilEarned],
          [t('SPECIAL_TOIL_EARNED'), summary.specialToilEarned],
          [t('WEEKEND_TOIL_TAKEN'), summary.weekendToilTaken],
          [t('SPECIAL_TOIL_TAKEN'), summary.specialToilTaken],
          [t('WEEKEND_TOIL_REMAINING'), summary.weekendToilLeft],
          [t('SPECIAL_TOIL_REMAINING'), summary.specialToilLeft],
          [t('TOTAL_TOIL_REMAINING'), summary.toilLeft],
        ];
    return isMobile ? (
      <SummaryTiles title={t('TOIL_SUMMARY')} cells={cells} />
    ) : (
      <SummaryTable cells={cells} />
    );
  }

  const cells: SummaryCell[] = [
    [t('LEAVE_TYPE'), category],
    [`${t('TOTAL')} ${category}`, summary.allocated, 'total'],
    ...(summary.leaveCarryForward
      ? ([[t('LEAVE_CARRY_FORWARD'), summary.leaveCarryForward, 'total']] as SummaryCell[])
      : []),
    [`${t('TAKEN')} ${category}`, summary.taken, 'taken'],
    ...(policy.toil === 'combined'
      ? ([
          [t('TOIL_EARNED'), summary.toilEarned, 'toil'],
          [t('TOIL_CF'), summary.toilCarryForward, 'toil'],
          [t('TOIL_REMAINING'), summary.toilLeft, 'remaining'],
        ] as SummaryCell[])
      : []),
    [`${t('REMAINING')} ${category}`, summary.balance, 'remaining'],
  ];
  return isMobile ? (
    <SummaryTiles title={t('LEAVE_SUMMARY')} cells={cells} />
  ) : (
    <SummaryTable cells={cells} />
  );
};

/**
 * One employee's leave sheet, as the legacy sheet showed it: the HR year, the per-category
 * allotment, the default leave summary and the running ledger of the days that moved it. Used by
 * Leave Manager (for the picked employee) and My Leave (for the signed-in user); `key` it by user id
 * so the term and page reset when the employee changes. `canPickTerm` offers the employee's earlier
 * HR terms.
 */
export const LeaveSheetView = ({
  userId,
  canPickTerm = false,
}: {
  userId: string;
  canPickTerm?: boolean;
}) => {
  const { t } = useTranslation();
  const [termId, setTermId] = useState<string | undefined>(undefined);
  const initialPageSize = useInitialPageSize(ROWS_PER_PAGE);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const { policy, error: policyError } = useLeaveSheetPolicy(userId);
  const { data: terms, isLoading: isTermsLoading } = useGetLeaveSheetTerms(userId, canPickTerm);
  const { data: sheet, isLoading, error } = useGetLeaveSheet(userId, termId, policy);

  const handlePaginationChange = useCallback((next: PaginationState) => setPagination(next), []);
  const handleTermChange = (next: string) => {
    setTermId(next);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setColumnFilters([]);
  };

  if (policyError) {
    return <p className="text-sm text-destructive">{t('LEAVE_SHEET_POLICY_LOAD_FAILED')}</p>;
  }
  if (!policy || isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-16 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }
  if (error)
    return (
      <p className="text-sm text-destructive">
        {error instanceof Error ? error.message : String(error)}
      </p>
    );
  if (!sheet) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          {t('NO_CURRENT_HR_TERM')}
        </CardContent>
      </Card>
    );
  }

  // The ledger is built whole in the browser, so the table filters and pages its rows itself.
  const columns = createLeaveSheetTableColumns({
    t,
    policy: sheet.policy,
    defaultCategory: sheet.summary?.defaultCategory ?? '',
  });
  const filteredCount = filterRowsByColumns(sheet.rows, columns, columnFilters).length;

  return (
    <div className="flex flex-col gap-4">
      {/* On a phone the term picker, then "HR Year Start: date" and "HR Year End: date" lines. */}
      <div className="flex flex-wrap items-center justify-end gap-4 rounded-lg border bg-muted/40 p-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
        {canPickTerm && isTermsLoading && <Skeleton className="h-9 w-full md:w-56" />}
        {canPickTerm && terms && terms.length > 0 && (
          <Select
            value={termId ?? terms.find((term) => term.isCurrent)?.ItemId ?? terms[0].ItemId}
            onValueChange={handleTermChange}
          >
            <SelectTrigger className="w-full md:w-56" aria-label={t('SELECT_HR_TERM')}>
              <SelectValue placeholder={t('SELECT_HR_TERM')} />
            </SelectTrigger>
            <SelectContent>
              {terms.map((term) => (
                <SelectItem key={term.ItemId} value={term.ItemId}>
                  {termLabel(term, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="flex flex-col items-center gap-1 max-md:flex-row max-md:gap-1.5">
          <span className="text-sm text-muted-foreground">
            {t('HR_YEAR_START')}
            <span className="md:hidden">:</span>
          </span>
          <span className="text-sm font-medium">
            {formatDateOnly(toDateOnlyValue(sheet.term.periodStart))}
          </span>
        </div>
        <div className="h-10 w-px bg-border max-md:hidden" />
        <div className="flex flex-col items-center gap-1 max-md:flex-row max-md:gap-1.5">
          <span className="text-sm text-muted-foreground">
            {t('HR_YEAR_END')}
            <span className="md:hidden">:</span>
          </span>
          <span className="text-sm font-medium">
            {formatDateOnly(toDateOnlyValue(sheet.term.periodEnd))}
          </span>
        </div>
      </div>

      <CategoryTable sheet={sheet} />
      {sheet.summary ? (
        <LeaveSummary sheet={sheet} />
      ) : (
        <p className="text-sm text-muted-foreground">{t('LEAVE_SHEET_NO_DEFAULT_CATEGORY')}</p>
      )}

      {sheet.summary && (
        <div className="md:hidden">
          <MobileHeading>{t('LEAVES')}</MobileHeading>
        </div>
      )}
      {sheet.summary && (
        <AdvanceDataTable
          data={sheet.rows}
          columns={columns}
          isLoading={false}
          error={null}
          pagination={{
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            totalCount: filteredCount,
          }}
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          isExpandRowContent={false}
          onPaginationChange={handlePaginationChange}
          mobileView="cards"
        />
      )}
    </div>
  );
};
